import React from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { Card } from '@/components/ui/card';
import { Building2, Home, ShoppingBag, UtensilsCrossed, Briefcase } from 'lucide-react';

const ProjectTypeStep = ({ data, onChange }) => {
  const { t } = useLanguage();
  
  const projectTypes = [
    {
      id: 'residential_remodel',
      icon: Home,
      color: 'from-blue-500 to-cyan-500',
      descriptionKey: 'residentialRemodelDesc'
    },
    {
      id: 'residential_new',
      icon: Home,
      color: 'from-green-500 to-emerald-500',
      descriptionKey: 'residentialNewDesc'
    },
    {
      id: 'tenant_improvement_food',
      icon: UtensilsCrossed,
      color: 'from-orange-500 to-red-500',
      descriptionKey: 'tenantFoodDesc'
    },
    {
      id: 'tenant_improvement_retail',
      icon: ShoppingBag,
      color: 'from-purple-500 to-pink-500',
      descriptionKey: 'tenantRetailDesc'
    },
    {
      id: 'tenant_improvement_office',
      icon: Briefcase,
      color: 'from-indigo-500 to-blue-500',
      descriptionKey: 'tenantOfficeDesc'
    },
    {
      id: 'commercial_new',
      icon: Building2,
      color: 'from-slate-600 to-slate-800',
      descriptionKey: 'commercialNewDesc'
    },
  ];
  
  const generateDefaultSheets = (projectType) => {
    const base = [
      { sheet_number: 'G0.00', sheet_type: 'cover', title: 'COVER SHEET', included: true, order: 0, content_blocks: [] },
      { sheet_number: 'G1.00', sheet_type: 'general_notes', title: 'GENERAL NOTES', included: true, order: 1, content_blocks: [] },
    ];
    
    if (projectType === 'residential_remodel' || projectType === 'residential_new') {
      return [...base,
        { sheet_number: 'A1.00', sheet_type: 'site_plan', title: 'SITE PLAN', included: true, order: 2, content_blocks: [] },
        { sheet_number: 'A4.10', sheet_type: 'floor_plan', title: 'FLOOR PLAN', included: true, order: 3, content_blocks: [] },
        { sheet_number: 'A5.00', sheet_type: 'elevations', title: 'ELEVATIONS', included: true, order: 4, content_blocks: [] },
        { sheet_number: 'A6.00', sheet_type: 'schedules', title: 'SCHEDULES', included: true, order: 5, content_blocks: [] },
      ];
    } else {
      return [...base,
        { sheet_number: 'G0.10', sheet_type: 'life_safety', title: 'OCCUPANCY & EXITING STUDY', included: true, order: 2, content_blocks: [] },
        { sheet_number: 'G0.20', sheet_type: 'renderings', title: 'RENDERINGS', included: true, order: 3, content_blocks: [] },
        { sheet_number: 'G1.20', sheet_type: 'accessibility', title: 'ACCESSIBILITY DETAILS', included: true, order: 4, content_blocks: [] },
        { sheet_number: 'G1.30', sheet_type: 'green_building', title: 'GREEN BUILDING CODE', included: true, order: 5, content_blocks: [] },
        { sheet_number: 'A2.00', sheet_type: 'floor_plan', title: 'CONSTRUCTION FLOOR PLAN', included: true, order: 6, content_blocks: [] },
        { sheet_number: 'A6.00', sheet_type: 'schedules', title: 'SCHEDULES', included: true, order: 7, content_blocks: [] },
      ];
    }
  };
  
  const handleSelectType = (typeId) => {
    const sheets = generateDefaultSheets(typeId);
    onChange({ ...data, project_type: typeId, sheets: sheets });
  };
  
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">
          {t('projectType')}
        </h2>
        <p className="text-slate-600 dark:text-slate-400">
          {t('selectProjectType')}
        </p>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {projectTypes.map(type => {
          const Icon = type.icon;
          const isSelected = data.project_type === type.id;
          
          return (
            <Card
              key={type.id}
              className={`p-6 cursor-pointer transition-all hover:shadow-lg border border-slate-200 dark:border-slate-700 bg-white ${
                isSelected
                  ? 'ring-2 ring-blue-600 shadow-lg'
                  : 'hover:ring-2 hover:ring-slate-200'
              }`}
              onClick={() => handleSelectType(type.id)}
              data-testid={`project-type-${type.id}`}
            >
              <div className="flex items-start space-x-4">
                <div className={`p-3 rounded-lg bg-gradient-to-br ${type.color}`}>
                  <Icon className="h-6 w-6 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-slate-900 dark:text-white mb-1">
                    {t(type.id)}
                  </h3>
                  <p className="text-sm text-slate-600 dark:text-slate-400">
                    {t(type.descriptionKey)}
                  </p>
                </div>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default ProjectTypeStep;